import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Lock, LogOut, RefreshCcw, Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const STORAGE_KEY = 'admin_services_password';

const DEFAULT_SETTINGS = {
  businessName: '',
  businessTagline: '',
  contactEmail: '',
  contactPhone: '',
  locationLabel: '',
  serviceAreaLabel: '',
  footerDescription: '',
  bookingHelpText: '',
  officeHoursWeekdayLabel: 'Monday - Friday:',
  officeHoursWeekdayHours: '9:00 AM - 7:00 PM',
  officeHoursSaturdayLabel: 'Saturday:',
  officeHoursSaturdayHours: '10:00 AM - 4:00 PM',
  officeHoursSundayLabel: 'Sunday:',
  officeHoursSundayHours: 'By Appointment',
  whatsappNumber: '',
  whatsappMessage: "Hello, I'd like to book a session.",
  featuredImageUrl: 'https://images.unsplash.com/photo-1616849068479-376693e487d2',
  siteExcerpt: '',
  enableManualPayments: true,
  manualPaymentInstructions:
    'Manual payment is available after booking confirmation (for example bank transfer or cash).',
  enablePaypalPayments: false,
  paypalClientId: '',
  paypalEnvironment: 'sandbox',
  paypalCurrency: 'EUR',
  onlinePaymentTaxPercent: 19,
  onlinePaymentBookingFee: 2.5,
  onlinePaymentMinimumPercent: 50,
  cancellationWindowHours: 24,
  cancellationTerms:
    'A minimum booking payment of 50% is required when booking online, or you may pay the full amount. If a booking is cancelled within the cancellation window, no refund is provided.',
  bookingBufferMinutes: 60,
  offDays: [],
  privacyPolicyText: '',
  termsOfServiceText: '',
};

const normalizeNonNegativeNumber = (value, fallback) => {
  const parsed = Number(value);
  if (Number.isNaN(parsed) || parsed < 0) {
    return fallback;
  }
  return Math.round(parsed * 100) / 100;
};

const normalizeSettings = (payload = {}) => ({
  businessName: String(payload.businessName || '').trim(),
  businessTagline: String(payload.businessTagline || '').trim(),
  contactEmail: String(payload.contactEmail || '').trim(),
  contactPhone: String(payload.contactPhone || '').trim(),
  locationLabel: String(payload.locationLabel || '').trim(),
  serviceAreaLabel: String(payload.serviceAreaLabel || '').trim(),
  footerDescription: String(payload.footerDescription || '').trim(),
  bookingHelpText: String(payload.bookingHelpText || '').trim(),
  officeHoursWeekdayLabel: String(payload.officeHoursWeekdayLabel || '').trim(),
  officeHoursWeekdayHours: String(payload.officeHoursWeekdayHours || '').trim(),
  officeHoursSaturdayLabel: String(payload.officeHoursSaturdayLabel || '').trim(),
  officeHoursSaturdayHours: String(payload.officeHoursSaturdayHours || '').trim(),
  officeHoursSundayLabel: String(payload.officeHoursSundayLabel || '').trim(),
  officeHoursSundayHours: String(payload.officeHoursSundayHours || '').trim(),
  whatsappNumber: String(payload.whatsappNumber || '').trim(),
  whatsappMessage: String(payload.whatsappMessage || '').trim(),
  featuredImageUrl: String(payload.featuredImageUrl || '').trim(),
  siteExcerpt: String(payload.siteExcerpt || '').trim(),
  enableManualPayments: Boolean(payload.enableManualPayments),
  manualPaymentInstructions: String(payload.manualPaymentInstructions || '').trim(),
  enablePaypalPayments: Boolean(payload.enablePaypalPayments),
  paypalClientId: String(payload.paypalClientId || '').trim(),
  paypalEnvironment: String(payload.paypalEnvironment || 'sandbox').trim().toLowerCase() === 'live' ? 'live' : 'sandbox',
  paypalCurrency: String(payload.paypalCurrency || 'EUR')
    .replace(/[^A-Za-z]/g, '')
    .toUpperCase()
    .slice(0, 3) || 'EUR',
  onlinePaymentTaxPercent: normalizeNonNegativeNumber(payload.onlinePaymentTaxPercent, 19),
  onlinePaymentBookingFee: normalizeNonNegativeNumber(payload.onlinePaymentBookingFee, 2.5),
  onlinePaymentMinimumPercent: Math.min(
    100,
    Math.max(50, normalizeNonNegativeNumber(payload.onlinePaymentMinimumPercent, 50)),
  ),
  cancellationWindowHours: Math.max(
    1,
    Math.round(normalizeNonNegativeNumber(payload.cancellationWindowHours, 24)),
  ),
  cancellationTerms: String(payload.cancellationTerms || '').trim(),
  bookingBufferMinutes: Math.max(
    0,
    Math.round(normalizeNonNegativeNumber(payload.bookingBufferMinutes, 60)),
  ),
  offDays: Array.isArray(payload.offDays)
    ? payload.offDays
        .map((item) => String(item || '').trim())
        .filter((item) => /^\d{4}-\d{2}-\d{2}$/.test(item))
    : [],
  privacyPolicyText: String(payload.privacyPolicyText || '').trim(),
  termsOfServiceText: String(payload.termsOfServiceText || '').trim(),
});

const requiredFields = [
  'businessName',
  'businessTagline',
  'contactEmail',
  'contactPhone',
  'locationLabel',
  'serviceAreaLabel',
  'footerDescription',
  'bookingHelpText',
  'officeHoursWeekdayLabel',
  'officeHoursWeekdayHours',
  'officeHoursSaturdayLabel',
  'officeHoursSaturdayHours',
  'officeHoursSundayLabel',
  'officeHoursSundayHours',
  'featuredImageUrl',
  'siteExcerpt',
  'privacyPolicyText',
  'termsOfServiceText',
];

const AdminSiteSettings = () => {
  const [siteSettings, setSiteSettings] = useState(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingFeaturedImage, setIsUploadingFeaturedImage] = useState(false);
  const [status, setStatus] = useState({ type: '', text: '' });

  const [adminPassword, setAdminPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);

  const verifyPassword = useCallback(async (password, silent = false) => {
    if (!password) {
      if (!silent) {
        setStatus({ type: 'error', text: 'Enter your admin password.' });
      }
      setIsCheckingAuth(false);
      setIsAuthenticated(false);
      return false;
    }

    setIsVerifying(true);

    try {
      const response = await fetch('/api/admin/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      if (!response.ok) {
        throw new Error(`Auth failed (${response.status})`);
      }

      sessionStorage.setItem(STORAGE_KEY, password);
      setAdminPassword(password);
      setIsAuthenticated(true);
      setStatus({ type: '', text: '' });
      return true;
    } catch (error) {
      console.error(error);
      sessionStorage.removeItem(STORAGE_KEY);
      setIsAuthenticated(false);
      if (!silent) {
        setStatus({ type: 'error', text: 'Invalid admin password.' });
      }
      return false;
    } finally {
      setIsVerifying(false);
      setIsCheckingAuth(false);
    }
  }, []);

  const loadSettings = useCallback(async () => {
    setIsLoading(true);
    setStatus({ type: '', text: '' });

    try {
      const response = await fetch('/api/content/site-settings', {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load site settings (${response.status})`);
      }

      const payload = await response.json();
      setSiteSettings(normalizeSettings({ ...DEFAULT_SETTINGS, ...payload }));
    } catch (error) {
      console.error(error);
      setStatus({
        type: 'error',
        text: 'Failed to load settings. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const storedPassword = sessionStorage.getItem(STORAGE_KEY);

    if (!storedPassword) {
      setIsCheckingAuth(false);
      return;
    }

    setAdminPassword(storedPassword);
    verifyPassword(storedPassword, true);
  }, [verifyPassword]);

  useEffect(() => {
    if (isAuthenticated) {
      loadSettings();
    }
  }, [isAuthenticated, loadSettings]);

  const updateField = (field, value) => {
    setSiteSettings((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleFeaturedImageUpload = async (event) => {
    const imageFile = event.target.files?.[0];
    if (!imageFile) return;

    setIsUploadingFeaturedImage(true);
    try {
      const formData = new FormData();
      formData.append('file', imageFile);
      const response = await fetch('/api/upload-image', {
        method: 'POST',
        headers: { 'X-Admin-Password': adminPassword },
        body: formData,
      });
      if (!response.ok) throw new Error(`Image upload failed (${response.status})`);
      const payload = await response.json();
      updateField('featuredImageUrl', payload.url || '');
      setStatus({ type: 'success', text: 'Featured image uploaded successfully.' });
    } catch (error) {
      console.error(error);
      setStatus({ type: 'error', text: error?.message || 'Failed to upload image.' });
    } finally {
      setIsUploadingFeaturedImage(false);
      event.target.value = '';
    }
  };

  const saveSettings = async () => {
    const payload = normalizeSettings(siteSettings);

    const missingField = requiredFields.find((fieldName) => !payload[fieldName]);
    if (missingField) {
      setStatus({
        type: 'error',
        text: 'Fill in all required fields before saving.',
      });
      return;
    }

    setIsSaving(true);
    setStatus({ type: '', text: '' });

    try {
      const response = await fetch('/api/content/site-settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Admin-Password': adminPassword,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        if (response.status === 401) {
          sessionStorage.removeItem(STORAGE_KEY);
          setIsAuthenticated(false);
          setStatus({ type: 'error', text: 'Admin session expired. Please sign in again.' });
          return;
        }

        throw new Error(`Failed to save site settings (${response.status})`);
      }

      const updated = await response.json();
      setSiteSettings(normalizeSettings(updated));
      setStatus({ type: 'success', text: 'Saved successfully.' });
    } catch (error) {
      console.error(error);
      setStatus({
        type: 'error',
        text: 'Failed to save settings. Please try again.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSignIn = async (event) => {
    event.preventDefault();
    await verifyPassword(adminPassword, false);
  };

  const handleSignOut = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setIsAuthenticated(false);
    setSiteSettings(DEFAULT_SETTINGS);
    setStatus({ type: '', text: '' });
  };

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-sage-50 py-12">
        <div className="container mx-auto px-4 max-w-lg">
          <Card className="border-sage-200">
            <CardContent className="p-6 text-sage-700">Checking admin session...</CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-sage-50 py-12">
        <div className="container mx-auto px-4 max-w-lg">
          <Card className="border-sage-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sage-900">
                <Lock className="w-5 h-5" />
                Admin Access
              </CardTitle>
              <CardDescription>
                Enter the admin password to edit site settings.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSignIn} className="space-y-4">
                <div>
                  <Label className="text-sage-700">Password</Label>
                  <Input
                    type="password"
                    value={adminPassword}
                    onChange={(event) => setAdminPassword(event.target.value)}
                    className="mt-1.5 border-sage-300"
                    autoFocus
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-sage-600 hover:bg-sage-700"
                  disabled={isVerifying}
                >
                  {isVerifying ? 'Verifying...' : 'Sign In'}
                </Button>
                {status.text && (
                  <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                    {status.text}
                  </p>
                )}
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sage-50 py-12">
      <div className="container mx-auto px-4 max-w-5xl space-y-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-sage-900">Site Settings Admin</h1>
            <p className="text-sage-600 mt-1">
              Edit global details, office hours, legal pages, and WhatsApp chat settings.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Link to="/admin">
              <Button type="button" variant="outline" className="border-sage-300">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Admin Home
              </Button>
            </Link>
            <Button
              type="button"
              variant="outline"
              onClick={handleSignOut}
              className="border-sage-300"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={loadSettings}
              disabled={isLoading || isSaving}
              className="border-sage-300"
            >
              <RefreshCcw className="w-4 h-4 mr-2" />
              Reload
            </Button>
            <Button
              type="button"
              onClick={saveSettings}
              disabled={isLoading || isSaving}
              className="bg-sage-600 hover:bg-sage-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>

        {status.text && (
          <div
            className={`rounded-lg px-4 py-3 text-sm font-medium ${
              status.type === 'success'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}
          >
            {status.text}
          </div>
        )}

        <Card className="border-sage-200 shadow-sm">
          <CardHeader className="bg-white border-b border-sage-200">
            <CardTitle className="text-sage-900">Business & Contact</CardTitle>
            <CardDescription>
              Used in header, footer, booking, and contact pages.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5 bg-white pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sage-700">Business Name</Label>
                <Input
                  value={siteSettings.businessName}
                  onChange={(event) => updateField('businessName', event.target.value)}
                  className="mt-1.5 border-sage-300"
                />
              </div>
              <div>
                <Label className="text-sage-700">Business Tagline</Label>
                <Input
                  value={siteSettings.businessTagline}
                  onChange={(event) => updateField('businessTagline', event.target.value)}
                  className="mt-1.5 border-sage-300"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sage-700">Contact Email</Label>
                <Input
                  type="email"
                  value={siteSettings.contactEmail}
                  onChange={(event) => updateField('contactEmail', event.target.value)}
                  className="mt-1.5 border-sage-300"
                />
              </div>
              <div>
                <Label className="text-sage-700">Contact Phone</Label>
                <Input
                  value={siteSettings.contactPhone}
                  onChange={(event) => updateField('contactPhone', event.target.value)}
                  className="mt-1.5 border-sage-300"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sage-700">Location Label</Label>
                <Input
                  value={siteSettings.locationLabel}
                  onChange={(event) => updateField('locationLabel', event.target.value)}
                  className="mt-1.5 border-sage-300"
                />
              </div>
              <div>
                <Label className="text-sage-700">Service Area Label</Label>
                <Input
                  value={siteSettings.serviceAreaLabel}
                  onChange={(event) => updateField('serviceAreaLabel', event.target.value)}
                  className="mt-1.5 border-sage-300"
                />
              </div>
            </div>

            <div>
              <Label className="text-sage-700">Footer Description</Label>
              <Textarea
                value={siteSettings.footerDescription}
                onChange={(event) => updateField('footerDescription', event.target.value)}
                className="mt-1.5 border-sage-300 min-h-20"
              />
            </div>

            <div>
              <Label className="text-sage-700">Booking Help Text</Label>
              <Textarea
                value={siteSettings.bookingHelpText}
                onChange={(event) => updateField('bookingHelpText', event.target.value)}
                className="mt-1.5 border-sage-300 min-h-20"
              />
            </div>

            <div>
              <Label className="text-sage-700">Site Excerpt</Label>
              <Textarea
                value={siteSettings.siteExcerpt}
                onChange={(event) => updateField('siteExcerpt', event.target.value)}
                className="mt-1.5 border-sage-300 min-h-20"
              />
              <p className="text-xs text-sage-500 mt-1">
                Used as site description for search and social sharing.
              </p>
            </div>

            <div>
              <Label className="text-sage-700">Featured Image URL</Label>
              <Input
                value={siteSettings.featuredImageUrl}
                onChange={(event) => updateField('featuredImageUrl', event.target.value)}
                placeholder="https://..."
                className="mt-1.5 border-sage-300"
              />
              <p className="text-xs text-sage-500 mt-1">
                Used as social preview/featured image (Open Graph and Twitter cards).
              </p>
              <Input type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={handleFeaturedImageUpload} disabled={isUploadingFeaturedImage || isSaving} className="mt-3 border-sage-300" />
              {siteSettings.featuredImageUrl && <img src={siteSettings.featuredImageUrl} alt="Featured image preview" className="mt-3 h-32 w-full max-w-md rounded-md border border-sage-200 object-cover" />}
            </div>
          </CardContent>
        </Card>

        <Card className="border-sage-200 shadow-sm">
          <CardHeader className="bg-white border-b border-sage-200">
            <CardTitle className="text-sage-900">Office Hours & WhatsApp</CardTitle>
            <CardDescription>
              Office hours appear on contact page. WhatsApp settings control the floating chat button.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5 bg-white pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sage-700">Weekday Label</Label>
                <Input
                  value={siteSettings.officeHoursWeekdayLabel}
                  onChange={(event) => updateField('officeHoursWeekdayLabel', event.target.value)}
                  className="mt-1.5 border-sage-300"
                />
              </div>
              <div>
                <Label className="text-sage-700">Weekday Hours</Label>
                <Input
                  value={siteSettings.officeHoursWeekdayHours}
                  onChange={(event) => updateField('officeHoursWeekdayHours', event.target.value)}
                  className="mt-1.5 border-sage-300"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sage-700">Saturday Label</Label>
                <Input
                  value={siteSettings.officeHoursSaturdayLabel}
                  onChange={(event) => updateField('officeHoursSaturdayLabel', event.target.value)}
                  className="mt-1.5 border-sage-300"
                />
              </div>
              <div>
                <Label className="text-sage-700">Saturday Hours</Label>
                <Input
                  value={siteSettings.officeHoursSaturdayHours}
                  onChange={(event) => updateField('officeHoursSaturdayHours', event.target.value)}
                  className="mt-1.5 border-sage-300"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sage-700">Sunday Label</Label>
                <Input
                  value={siteSettings.officeHoursSundayLabel}
                  onChange={(event) => updateField('officeHoursSundayLabel', event.target.value)}
                  className="mt-1.5 border-sage-300"
                />
              </div>
              <div>
                <Label className="text-sage-700">Sunday Hours</Label>
                <Input
                  value={siteSettings.officeHoursSundayHours}
                  onChange={(event) => updateField('officeHoursSundayHours', event.target.value)}
                  className="mt-1.5 border-sage-300"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sage-700">WhatsApp Number</Label>
                <Input
                  value={siteSettings.whatsappNumber}
                  onChange={(event) => updateField('whatsappNumber', event.target.value)}
                  placeholder="+49 ..."
                  className="mt-1.5 border-sage-300"
                />
              </div>
              <div>
                <Label className="text-sage-700">WhatsApp Prefilled Message</Label>
                <Input
                  value={siteSettings.whatsappMessage}
                  onChange={(event) => updateField('whatsappMessage', event.target.value)}
                  className="mt-1.5 border-sage-300"
                />
              </div>
            </div>

            <p className="text-xs text-sage-500">
              Tip: leave WhatsApp number empty to hide the floating chat button.
            </p>
          </CardContent>
        </Card>

        <Card className="border-sage-200 shadow-sm">
          <CardHeader className="bg-white border-b border-sage-200">
            <CardTitle className="text-sage-900">Legal Pages</CardTitle>
            <CardDescription>
              These texts are displayed on Privacy Policy and Terms of Service pages.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5 bg-white pt-6">
            <div>
              <Label className="text-sage-700">Privacy Policy Text</Label>
              <Textarea
                value={siteSettings.privacyPolicyText}
                onChange={(event) => updateField('privacyPolicyText', event.target.value)}
                className="mt-1.5 border-sage-300 min-h-44"
              />
            </div>
            <div>
              <Label className="text-sage-700">Terms of Service Text</Label>
              <Textarea
                value={siteSettings.termsOfServiceText}
                onChange={(event) => updateField('termsOfServiceText', event.target.value)}
                className="mt-1.5 border-sage-300 min-h-44"
              />
            </div>

            <p className="text-xs text-sage-500">
              Use blank lines between paragraphs for cleaner formatting on public pages.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminSiteSettings;
